import React, { useEffect, useState } from 'react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';

// Bingo grid — each cell holds text or an image; students tap a cell to cross
// it out with a big X. Config and state are decoupled: content lives in
// `element.content.cells` (editor-authored), crossed state lives either in
// local component state (editor preview) or externally via syncedCrossedIds.
const BingoElement = ({
  element,
  isEditor = false,
  canInteract = true,
  onCrossedChange,
  syncedCrossedIds = null,
}) => {
  const { content = {} } = element;
  const rows = Math.max(1, Math.min(8, Number(content.rows) || 3));
  const cols = Math.max(1, Math.min(8, Number(content.cols) || 3));
  const expectedCount = rows * cols;
  const authoredCells = Array.isArray(content.cells) ? content.cells : [];
  const cells = Array.from({ length: expectedCount }, (_, idx) => {
    const existing = authoredCells[idx];
    return existing || { id: `cell_${idx}`, type: 'text', text: '', imageUrl: null };
  });

  const cellBg = content.cellBg || '#ffffff';
  const textColor = content.textColor || '#1f2937';
  const borderColor = content.borderColor || '#2A4D25';
  const borderWidth = Number.isFinite(content.borderWidth) ? content.borderWidth : 2;
  const crossColor = content.crossColor || '#dc2626';
  const gap = Number.isFinite(content.gap) ? content.gap : 4;
  const fontFamily = content.fontFamily || 'Evolventa';
  const fontSize = Number.isFinite(content.fontSize) ? content.fontSize : 16;

  const [localCrossed, setLocalCrossed] = useState(() =>
    Array.isArray(syncedCrossedIds) ? syncedCrossedIds : []
  );

  useEffect(() => {
    if (Array.isArray(syncedCrossedIds)) {
      setLocalCrossed(syncedCrossedIds);
    }
  }, [syncedCrossedIds]);

  const crossedSet = new Set(localCrossed);

  const handleCellClick = (cellId) => {
    if (isEditor || !canInteract) return;
    const next = crossedSet.has(cellId)
      ? localCrossed.filter((id) => id !== cellId)
      : [...localCrossed, cellId];
    setLocalCrossed(next);
    if (onCrossedChange) onCrossedChange(next);
  };

  return (
    <div
      className="w-full h-full"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: `${gap}px`,
      }}
    >
      {cells.map((cell) => {
        const isCrossed = crossedSet.has(cell.id);
        const hasImage = cell.type === 'image' && cell.imageUrl;
        return (
          <div
            key={cell.id}
            onClick={() => handleCellClick(cell.id)}
            className="relative overflow-hidden select-none"
            style={{
              backgroundColor: cell.bgColor || cellBg,
              border: `${borderWidth}px solid ${borderColor}`,
              borderRadius: 8,
              cursor: canInteract && !isEditor ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4,
              boxSizing: 'border-box',
            }}
          >
            {hasImage ? (
              <img
                src={resolveMediaUrl(cell.imageUrl)}
                alt=""
                className="max-w-full max-h-full object-contain pointer-events-none"
                draggable={false}
              />
            ) : (
              <span
                className="text-center break-words leading-tight pointer-events-none"
                style={{
                  color: cell.textColor || textColor,
                  fontFamily,
                  fontSize: `${fontSize}px`,
                }}
              >
                {cell.text || ''}
              </span>
            )}

            {isCrossed && (
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full pointer-events-none"
                aria-hidden="true"
              >
                <line
                  x1="8"
                  y1="8"
                  x2="92"
                  y2="92"
                  stroke={crossColor}
                  strokeWidth="8"
                  strokeLinecap="round"
                />
                <line
                  x1="92"
                  y1="8"
                  x2="8"
                  y2="92"
                  stroke={crossColor}
                  strokeWidth="8"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BingoElement;
